import { Maybe, Prices, PricingRules } from '@gql/schema';
import { IPriceValueTranslationData } from '@secretescapes/cauldron';
import { ITranslationsObject, i18n } from '@secretescapes/i18n';
import { DayDataConfig } from '@features/AvailabilityPresentation/Calendar/Calendar.interfaces';

export const BOOKING_FEE_PERCENTAGE = 1.05;

const RULES_NOT_DISPLAYING_PRICES: PricingRules = {
  showPrices: false,
  showRackRate: false,
  showDiscount: false,
  showUrgentDiscount: false,
};

export const mergePricingRules = (
  kingfisherPricingRules: PricingRules | null | undefined,
  showPricingAndAvailability: boolean
): PricingRules => {
  if (!kingfisherPricingRules || !showPricingAndAvailability) {
    return RULES_NOT_DISPLAYING_PRICES;
  }

  const mergedPricingRules: PricingRules = {
    showPrices: !!kingfisherPricingRules.showPrices,
    showRackRate: !!kingfisherPricingRules.showRackRate,
    showDiscount: !!kingfisherPricingRules.showDiscount,
    showUrgentDiscount: !!kingfisherPricingRules.showUrgentDiscount,
  };

  return mergedPricingRules;
};

export const numberToCurrency = (
  number: number,
  currency: string,
  territory: string,
  noMinorUnits: boolean
) => {
  return new Intl.NumberFormat(territory, {
    style: 'currency',
    currency,
    minimumFractionDigits: noMinorUnits ? 0 : 2,
    maximumFractionDigits: noMinorUnits ? 0 : 2,
  }).format(number);
};

export const averagePricePerNight = (totalPrice: number, nights: number, config: DayDataConfig) => {
  return numberToCurrency(Math.ceil(totalPrice / nights), config.currency, config.territory, true);
};

export const getPriceWithBookingFee = (price: number) => {
  return Math.ceil(price * BOOKING_FEE_PERCENTAGE);
};

export const getFormattedPriceWithBookingFee = (
  price: number,
  currency: string,
  territory: string
) => {
  return numberToCurrency(getPriceWithBookingFee(price), currency, territory, true);
};

interface GetNavLeadPriceLabellingArgs {
  pricing: Maybe<Prices>;
  flightsSelected: boolean;
  translations: ITranslationsObject;
}

export const getNavLeadPriceLabelling = ({
  pricing,
  flightsSelected,
  translations,
}: GetNavLeadPriceLabellingArgs): IPriceValueTranslationData => {
  const { withFlights } = pricing ?? {};

  const fromLabel = i18n('common.fromPrice.label', [], translations);

  if (flightsSelected) {
    const tooltip = i18n(
      'flights.price.tooltip',
      [withFlights?.departureAirportName ?? '', 2, withFlights?.duration ?? 0],
      translations
    );
    const travellersLabel = i18n('pricing.travellers.label', [2], translations);
    const minimumNightsLabel = i18n(
      'common.for.nights.text',
      [withFlights?.duration ?? 0],
      translations
    );

    return {
      priceValueHint: `${travellersLabel}, ${minimumNightsLabel}`,
      priceValuePrefixLabel: fromLabel,
      priceValueSuffixLabel: '',
      leadRateTooltip: tooltip,
    };
  }

  const roomUnitLabel = i18n('pricing.room.unit.label', [], translations);
  const minimumNightsLabel = i18n(
    'common.for.nights.text',
    [pricing?.numberOfHotelNights ?? 0],
    translations
  );

  const fullUnitLabel =
    (pricing?.numberOfHotelNights ?? 0) > 1
      ? `${roomUnitLabel}, ${minimumNightsLabel}`
      : roomUnitLabel;

  return {
    priceValueHint: fullUnitLabel,
    priceValuePrefixLabel: fromLabel,
    priceValueSuffixLabel: '',
    leadRateTooltip: pricing?.leadRateTooltip ?? '',
  };
};

interface GetNavTotalPriceLabellingArgs {
  unitPrice: string;
  numberOfTravellers: number;
  numberOfNights: number;
  flightsSelected: boolean;
  translations: ITranslationsObject;
}

export const getNavTotalPriceLabelling = ({
  unitPrice,
  numberOfTravellers,
  numberOfNights,
  flightsSelected,
  translations,
}: GetNavTotalPriceLabellingArgs): IPriceValueTranslationData => {
  const fromLabel = i18n('common.fromPrice.label', [], translations);

  if (flightsSelected) {
    const travellersLabel = i18n(
      numberOfTravellers > 1 ? 'pricing.travellers.label' : 'pricing.traveller.label',
      [numberOfTravellers],
      translations
    );
    const nightsLabel = i18n('common.nights.text', [numberOfNights], translations);

    return {
      priceValueHint: `${travellersLabel}, ${nightsLabel}`,
      priceValuePrefixLabel: fromLabel,
      priceValueSuffixLabel: '',
      leadRateTooltip: '',
    };
  }

  const roomUnitLabel = i18n('pricing.room.unit.label', [], translations);

  return {
    priceValueHint: `${unitPrice} ${roomUnitLabel}`,
    priceValuePrefixLabel: fromLabel,
    priceValueSuffixLabel: '',
    leadRateTooltip: '',
  };
};

interface GetTabPriceLabellingArgs {
  pricing: Maybe<Prices>;
  flightsSelected: boolean;
  translations: ITranslationsObject;
}

export const getTabPriceLabelling = ({
  pricing,
  flightsSelected,
  translations,
}: GetTabPriceLabellingArgs): IPriceValueTranslationData => {
  const { withFlights } = pricing ?? {};
  const fromLabel = i18n('common.fromPrice.label', [], translations);

  if (flightsSelected) {
    const tooltip = i18n(
      'flights.price.tooltip',
      [withFlights?.departureAirportName ?? '', 2, withFlights?.duration ?? 0],
      translations
    );
    const travellersLabel = i18n('pricing.travellers.label', [2], translations);
    const minimumNightsLabel = i18n(
      'common.for.nights.text',
      [withFlights?.duration ?? 0],
      translations
    );

    return {
      priceValueHint: `${travellersLabel}, ${minimumNightsLabel}`,
      priceValuePrefixLabel: fromLabel,
      priceValueSuffixLabel: '',
      leadRateTooltip: tooltip,
    };
  }

  const roomUnitLabel = i18n('pricing.room.unit.label', [], translations);
  const minimumNightsLabel = i18n(
    'common.for.nights.text',
    [pricing?.numberOfHotelNights ?? 0],
    translations
  );

  const fullUnitLabel =
    (pricing?.numberOfHotelNights ?? 0) > 1
      ? `${roomUnitLabel}, ${minimumNightsLabel}`
      : roomUnitLabel;

  return {
    priceValueHint: fullUnitLabel,
    priceValuePrefixLabel: fromLabel,
    priceValueSuffixLabel: '',
    leadRateTooltip: '',
  };
};

interface GetOfferLeadPriceLabellingArgs {
  flightsSelected: boolean;
  numberOfTravellers: number;
  roomType: string;
  minimumNights: number;
  showDisclaimer: boolean;
  translations: ITranslationsObject;
}

export const getOfferLeadPriceLabelling = ({
  flightsSelected,
  numberOfTravellers,
  roomType,
  minimumNights,
  showDisclaimer,
  translations,
}: GetOfferLeadPriceLabellingArgs): IPriceValueTranslationData => {
  const travellersKey =
    numberOfTravellers > 1 ? 'pricing.travellers.label' : 'pricing.traveller.label';
  const travellersLabel = i18n(travellersKey, [numberOfTravellers], translations);
  const minimumNightsLabel = i18n('common.for.nights.text', [minimumNights], translations);
  const fromLabel = i18n('common.fromPrice.label', [], translations);

  if (flightsSelected) {
    return {
      priceValueHint:
        minimumNights > 1 ? `${travellersLabel}, ${minimumNightsLabel}` : travellersLabel,
      priceValuePrefixLabel: fromLabel,
      priceValueSuffixLabel: '',
      leadRateTooltip: '',
    };
  }

  const roomUnitLabel = i18n('pricing.roomType.unit.label', [roomType], translations);
  const basedOnLabel = `${i18n('pricing.based.on.travellers.label', [], translations)} ${travellersLabel.split(' ').slice(1).join(' ')}`;
  const unitLabel = showDisclaimer ? `${roomUnitLabel}, ${basedOnLabel}` : roomUnitLabel;
  const fullUnitLabel = minimumNights > 1 ? `${unitLabel}, ${minimumNightsLabel}` : unitLabel;

  return {
    priceValueHint: fullUnitLabel,
    priceValuePrefixLabel: fromLabel,
    priceValueSuffixLabel: '',
    leadRateTooltip: '',
  };
};

interface GetOfferTotalPriceLabellingArgs {
  unitPrice: string;
  numberOfRooms: number;
  numberOfTravellers: number;
  numberOfNights: number;
  roomType: string;
  roomTypePlural: string;
  flightsSelected: boolean;
  translations: ITranslationsObject;
}

export const getOfferTotalPriceLabelling = ({
  unitPrice,
  numberOfRooms,
  numberOfTravellers,
  numberOfNights,
  roomType,
  roomTypePlural,
  flightsSelected,
  translations,
}: GetOfferTotalPriceLabellingArgs): IPriceValueTranslationData => {
  const nightsLabel = i18n('common.nights.text', [numberOfNights], translations);

  if (flightsSelected) {
    const travellersLabel = i18n(
      numberOfTravellers > 1 ? 'pricing.travellers.label' : 'pricing.traveller.label',
      [numberOfTravellers],
      translations
    );
    const totalLabel = i18n('price.label.total', [], translations);

    return {
      priceValueHint: `${travellersLabel}, ${nightsLabel}`,
      priceValuePrefixLabel: '',
      priceValueSuffixLabel: totalLabel,
      leadRateTooltip: '',
    };
  }

  const roomUnitLabel = i18n('pricing.roomType.unit.label', [roomType], translations);
  const roomsLabel = i18n(
    'pricing.roomType.label',
    [numberOfRooms, numberOfRooms > 1 ? roomTypePlural : roomType],
    translations
  );

  return {
    priceValueHint: `${unitPrice} ${roomUnitLabel}`,
    priceValuePrefixLabel: '',
    priceValueSuffixLabel: `${roomsLabel}, ${nightsLabel}`,
    leadRateTooltip: '',
  };
};
